import { useParams, useLocation } from "react-router-dom";
import { useMemo, useState, useRef } from "react";
import { Popover, Whisper } from "rsuite";
import { useAtomValue } from "jotai";
import { Button } from "reactstrap";

import BasicSharedRecordDrawer from "../Drawers/ShareRecordDrawer/BasicSharedRecordDrawer";
import useCommentParams from "../Drawers/CommentsDrawer/useCommentParams";
import ThumbnailWidget from "../Widgets/ThumbnailWidget/ThumbnailWidget";
import CommentsDrawer from "../Drawers/CommentsDrawer/CommentsDrawer";
import ViewRecordDrawer from "../Drawers/Records/ViewRecordDrawer";
import { getMetadataFromRecord } from "../../utils/RecordUtils";
import useUser from "../../api/api-hooks/auth/useUser";
import { Module } from "../../types/TenantInterfaces";
import useToggle from "../../hooks/useToggle";
import useModule from "../../hooks/useModule";
import CommentsWidget from "./CommentsWidget";
import useHover from "../../hooks/useHover";
import { IsMobileAtom } from "../../App";

export type ImageFieldPresentation = "Logo" | "Avatar" | null;

interface RecordNameCelProps {
  record: Record<string, any>;
  onOpen: ((module: Module, record: Record<string, any>) => void) | null;
  imageField: string | null;
  imageFieldPresentation: ImageFieldPresentation;
  disableViewRecord: boolean;
  metadataField: string | null;
  metadataFieldType: string | null;
  maxWidth?: number;
  fontSize?: number;
  defaultRecordLayout?: string;
  wrap?: boolean;
  className?: string;
  commentsWidget?: boolean;
  nameField?: string;
  emptyText?: string;
  stopPropagation?: boolean;
}

const RecordNameCell = ({
  record,
  onOpen,
  imageField = null,
  imageFieldPresentation,
  disableViewRecord = false,
  metadataField = null,
  metadataFieldType = null,
  maxWidth = 300,
  fontSize,
  defaultRecordLayout,
  wrap = false,
  className,
  commentsWidget = false,
  nameField = "_RecordName",
  emptyText = "(No Name)",
  stopPropagation = true,
}: RecordNameCelProps) => {
  const mobileScreen = useAtomValue(IsMobileAtom);
  const [showViewRecordDrawer, setShowViewRecordDrawer] = useState(false);
  const [viewRecordDrawer, toggleViewRecordDrawer] = useToggle();
  const [viewRecordID, setViewRecordID] = useState<string | null>(null);
  const { module } = useModule(record?._ModuleAPIName);
  const { tenantID, pageID, viewID } = useParams();
  const { search } = useLocation();
  const recordID = record?._id || record?.id || "";
  const [showComments, setShowComments] = useState(false);
  const commentRef = useRef<any>();
  const [itemRef, isHovered] = useHover<any>();
  const { open: commentsOpen, closeComments } = useCommentParams();
  const [openRecordCommentsDrawer, setOpenRecordCommentsDrawer] =
    useState(false);
  const [keepOpen, setKeepOpen] = useState(false);
  const { user } = useUser();
  const totalNotifications = useMemo(() => {
    return (
      user?.Notifications?.[tenantID ?? ""]?.RecordComments?.[recordID] ?? 0
    );
  }, [user?.Notifications?.[tenantID ?? ""]]);
  const [openShareRecordDrawer, toggleShareRecordDrawer] = useToggle();

  const metadata = useMemo(() => {
    if (!metadataField || !metadataFieldType || !record) return null;

    return getMetadataFromRecord(record, metadataField, metadataFieldType);
  }, [metadataField, metadataFieldType, record]);

  let url;

  if (pageID && viewID && !search) {
    url = `/clouds/${tenantID}/pages/${pageID}/views/${viewID}/records/${module?.APIName}/details/${recordID}/Summary`;
  } else {
    url = `/clouds/${tenantID}/records/${module?.APIName}/details/${recordID}/Summary`;
  }

  const RecordName = (
    <a
      href={url}
      onClick={(e) => {
        e.preventDefault();
        stopPropagation && e.stopPropagation();

        if (typeof onOpen === "function") {
          return onOpen(module!, record);
        }

        setShowViewRecordDrawer(true);
        toggleViewRecordDrawer();

        setViewRecordID(recordID);
      }}
      style={{ fontSize: mobileScreen ? "12px" : "" }}
      className={` ${wrap ? "" : "tw:truncate"} ${
        !onOpen && disableViewRecord ? "tw:cursor-default" : ""
      }`}
    >
      {record?.[nameField] ? record?.[nameField] : emptyText}
    </a>
  );

  // Used when the commentsWidget prop is true
  const CommentsButton = commentsWidget ? (
    <Button
      size="sm"
      className={`btn-icon-only text-medium`}
      style={{
        position: "fixed",
        right: !!record?._Synced ? "30px" : "-3px",
        height: 28,
        width: 28,
        transform: "translateY(-50%)",
      }}
      onClick={(e) => {
        e.stopPropagation();
        commentRef.current?.open?.();
      }}
    >
      <i className="mdi mdi-comment-outline" style={{ fontSize: 25 }} />
      {!!totalNotifications && (
        <div className="position-absolute">
          <div
            className="position-absolute bg-primary text-white rounded"
            style={{
              top: -20,
              left: totalNotifications > 9 ? 0 : 14,
              lineHeight: 1.1,
              fontSize: 10,
              padding: "0 4px",
            }}
          >
            {totalNotifications}
          </div>
        </div>
      )}
    </Button>
  ) : null;

  const BaseRecordNameCell = (
    <div
      className={`${
        wrap ? "" : "text-truncate"
      } tw:flex tw:justify-between tw:items-center`}
      style={{ position: "relative" }}
      ref={commentsWidget ? itemRef : undefined}
    >
      <div
        className={`${wrap ? "" : "text-truncate"} ${
          !!imageField || !!record?._Synced || !!commentsWidget
            ? "tw:flex tw:items-center"
            : ""
        } ${className || ""} `}
        style={{
          width: "100%",
          maxWidth: maxWidth,
          fontSize: fontSize ? fontSize : undefined,
        }}
        onClick={(e) => {
          e.preventDefault();
          stopPropagation && e.stopPropagation();

          if (typeof onOpen === "function") {
            return onOpen(module!, record);
          }

          setShowViewRecordDrawer(true);
          toggleViewRecordDrawer();

          setViewRecordID(recordID);
        }}
      >
        {!!imageField && (
          <ThumbnailWidget
            record={record}
            imageKey={imageField}
            mode={imageFieldPresentation}
          />
        )}
        {/* Handles both cases (No metadata, metadata) */}

        {!metadata && RecordName}
        {metadata && (
          <div
            className="text-truncate"
            style={{ fontSize: fontSize ? fontSize : undefined }}
          >
            {RecordName}
            <div className="mt--1" style={{ fontSize: 11 }}>
              {metadata}
            </div>
          </div>
        )}
        <div>{isHovered && CommentsButton}</div>
      </div>
      {!!record?._Synced && (
        <Button
          className="btn-icon-only text-medium"
          outline={true}
          size="sm"
          onClick={toggleShareRecordDrawer}
          style={{ height: 28, width: 28 }}
        >
          <i
            className="mdi mdi-cloud-sync-outline text-info"
            style={{ fontSize: 22 }}
          />
        </Button>
      )}
    </div>
  );

  let FinalContent;

  if (commentsWidget) {
    FinalContent = (
      <Whisper
        trigger={showComments && !keepOpen ? "click" : "none"}
        onOpen={() => setShowComments(true)}
        onClose={() => setShowComments(false)}
        preventOverflow
        ref={commentRef}
        speaker={
          <Popover className={keepOpen ? "d-none" : ""}>
            <CommentsWidget
              recordID={recordID}
              recordName={record?._RecordName}
              moduleAPIName={module?.APIName ?? ""}
              setOpenRecordCommentsDrawer={setOpenRecordCommentsDrawer}
              setKeepOpen={setKeepOpen}
              onOpenBrieCompose={() => {
                setKeepOpen(true);
              }}
              onCloseBrieCompose={() => {
                setKeepOpen(false);
              }}
            />
          </Popover>
        }
      >
        <div>{BaseRecordNameCell}</div>
      </Whisper>
    );
  } else {
    FinalContent = <div>{BaseRecordNameCell}</div>;
  }

  return (
    <>
      {FinalContent}
      {!disableViewRecord && showViewRecordDrawer && viewRecordID && (
        <ViewRecordDrawer
          open={viewRecordDrawer}
          onClose={toggleViewRecordDrawer}
          module={module!}
          onExited={() => {
            setShowViewRecordDrawer(false);
          }}
          recordID={viewRecordID}
          defaultRecordLayout={defaultRecordLayout}
        />
      )}
      {commentsWidget && commentsOpen && openRecordCommentsDrawer && (
        <CommentsDrawer
          open={commentsOpen && openRecordCommentsDrawer}
          onClose={() => {
            closeComments();
            setOpenRecordCommentsDrawer(false);
            setKeepOpen(false);
          }}
          context="Record"
          contextID={recordID}
          convoMetadata={{
            RecordName: record?._RecordName,
            RecordModule: module!?.APIName,
          }}
        />
      )}

      {!!record?._Synced && (
        <BasicSharedRecordDrawer
          open={openShareRecordDrawer}
          onClose={toggleShareRecordDrawer}
          recordID={record?._id}
          moduleAPIName={record?._ModuleAPIName}
        />
      )}
    </>
  );
};

export default RecordNameCell;
